import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../Components/Layout";
import Ribbon from "./Ribbon";
import { Category, getEntityFields } from "./Categories";
import { DataTable } from "../../Components/DataTable";
import { AddButton, Button } from "../../Components/Buttons";
import DeleteConfirmationModal from "../../Modals/DeleteConfirmationalModal";
import { ErrorSuccessModal } from "../../Modals/ErrorSuccessModal";
import { useTableColumns } from "./TableConfig";
import { useEntityAdapter } from "./useEntityAdapter";
import { entityIdKeyMap } from "../../EntityKeys";
import { PatientHeader } from "./Header";
import { GenericPrintModal } from "../../Components/GenericPrintTable";
import { getPrintConfig } from "./PrintConfig";

// Refactored Components
import { PatientInfoCard } from "./PatientInfoCard";
import { PatientFormModal } from "./PatientFormModal";
import {
  SurgeryRelatedDocuments,
  getDocumentsForSurgery,
} from "./SurgeryRelatedDocuments";
import type { PatientDocumentDto } from "../../types/patientDocument";

const PatientDetails: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const [category, setCategory] = useState<Category>("patient");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [editingId, setEditingId] = useState<number | null>(null);

  // States για το modal ελέγχου αρχείου εγγράφου
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docModalMessage, setDocModalMessage] = useState("");
  // Adapters
  const patientAdapter = useEntityAdapter("patient");
  const patientData = patientAdapter.data as Record<string, any> | undefined;

  const current = useEntityAdapter(category);
  const allergiesAdapter = useEntityAdapter("patientAllergy");
  const surgeriesAdapter = useEntityAdapter("patientSurgery");
  const documentsAdapter = useEntityAdapter("patientDocument");
  const navigate = useNavigate();

  const patientDocuments = useMemo(
    () =>
      Array.isArray(documentsAdapter.data)
        ? (documentsAdapter.data as PatientDocumentDto[])
        : [],
    [documentsAdapter.data],
  );

  const handleCategoryChange = (newCategory: Category) => {
    setSelectedIds([]);
    setFormData({});
    setEditingId(null);
    setCategory(newCategory);
  };

  const isPatientCategory = category === "patient";
  const isSurgeryCategory = category === "patientSurgery";
  const safeData = useMemo(() => {
    if (isPatientCategory) return [];
    return Array.isArray(current.data) ? current.data : [];
  }, [isPatientCategory, current.data]);

  const optionsPayload = useMemo(() => {
    if (category === "patientDocument") {
      return {
        documentTypes: current.options || [],
        surgeries: surgeriesAdapter.data || [],
      };
    }
    return current.options || [];
  }, [category, current.options, surgeriesAdapter.data]);

  const fields = useMemo(() => {
    return getEntityFields(category, false, optionsPayload) || [];
  }, [category, optionsPayload]);

  const handleDocumentClick = useCallback(
    (item: any) => {
      // ΕΛΕΓΧΟΣ: Αν δεν υπάρχει φυσικό αρχείο, άνοιξε το modal και σταμάτα!
      if (!item.filePath) {
        setDocModalMessage(
          "Αυτή η εγγραφή δεν περιέχει συνημμένο φυσικό αρχείο.",
        );
        setIsDocModalOpen(true);
        return;
      }

      const docId = item.documentId || item.id;
      if (docId) {
        navigate(`/documents/${docId}`, {
          state: {
            fileName: item.fileName,
            format: item.format,
            filePath: item.filePath,
          },
        });
      }
    },
    [navigate],
  );

  const getRelatedDocumentCount = useCallback(
    (surgery: any) => {
      const surgeryId = surgery?.surgeryId;
      if (!surgeryId) return 0;
      return getDocumentsForSurgery(patientDocuments, surgeryId).length;
    },
    [patientDocuments],
  );

  const handleRowClick = useCallback(
    (item: any) => {
      // 2. Υπάρχουσα λογική για τις υπόλοιπες κατηγορίες (άνοιγμα φόρμας edit)
      const idKey = entityIdKeyMap[category as keyof typeof entityIdKeyMap];
      if (!idKey) return;
      const rowId = item[idKey];

      if (rowId === undefined || rowId === null || rowId === 0) {
        console.error(
          `[ID Error]: Το ID για την κατηγορία "${category}" βρέθηκε undefined/0.`,
        );
        return;
      }

      setEditingId(Number(rowId));

      // Documents store linked surgeries as objects; the form expects id[].
      const editFormData =
        category === "patientDocument"
          ? {
              ...item,
              patientSurgeryIds:
                item.patientSurgeryIds ??
                item.patientSurgery?.map((s: any) => s.surgeryId) ??
                [],
            }
          : item;

      setFormData(editFormData);
      setIsModalOpen(true);
    },
    [category],
  );

  const columns = useTableColumns({
    data: safeData,
    category,
    fields,
    selectedIds,
    setSelectedIds,
    handleRowClick,
    getRelatedDocumentCount: isSurgeryCategory
      ? getRelatedDocumentCount
      : undefined,
  });

  const printConfig = useMemo(() => {
    return getPrintConfig(
      category,
      patientData,
      columns,
      safeData,
      optionsPayload,
    );
  }, [category, patientData, columns, safeData, optionsPayload]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetId = isPatientCategory
      ? patientId
        ? Number(patientId)
        : null
      : editingId;
    const payload = {
      ...formData,
      patientId: patientId ? Number(patientId) : undefined,
    };
    const entityAction = current as any;

    if (targetId !== null && entityAction.update) {
      await entityAction.update(targetId, payload);
    } else if (!isPatientCategory && entityAction.create) {
      await entityAction.create(payload);
    }

    closeFormModal();
  };

  const handleConfirmDelete = async () => {
    const entityAction = current as any;
    if (entityAction.remove) {
      await entityAction.remove(selectedIds as any);
    }
    setIsDeleteModalOpen(false);
    setSelectedIds([]);
  };

  const closeFormModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({});
  };

  return (
    <Layout>
      <div className="flex flex-col h-full min-w-0 p-6 space-y-6 overflow-x-auto">
        <PatientHeader
          lastName={patientData?.lastName}
          firstName={patientData?.firstName}
          amka={patientData?.amka}
          allergies={
            Array.isArray(allergiesAdapter.data) ? allergiesAdapter.data : []
          }
          loading={patientAdapter.isLoading || allergiesAdapter.isLoading}
        />

        <Ribbon activeCategory={category} onChange={handleCategoryChange} />

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold text-slate-800 capitalize">
              {category}
            </h2>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                onClick={() => setIsPrintModalOpen(true)}
              >
                🖨️ Εκτύπωση
              </Button>
              {isPatientCategory ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (patientData && patientId) {
                      setEditingId(Number(patientId));
                      setFormData(patientData);
                      setIsModalOpen(true);
                    }
                  }}
                  disabled={
                    !patientData || Object.keys(patientData).length === 0
                  }
                >
                  Τροποποίηση Στοιχείων
                </Button>
              ) : (
                <AddButton
                  label="Νέα Εγγραφή"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({});
                    setIsModalOpen(true);
                  }}
                />
              )}
            </div>
          </div>

          {isPatientCategory ? (
            <PatientInfoCard
              isLoading={patientAdapter.isLoading}
              patientData={patientData}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                data={safeData}
                loading={
                  current.isLoading ||
                  (isSurgeryCategory && documentsAdapter.isLoading)
                }
                onRowClick={
                  category === "patientDocument"
                    ? handleDocumentClick
                    : undefined
                }
                renderExpandedRow={
                  isSurgeryCategory
                    ? (surgery) => (
                        <SurgeryRelatedDocuments
                          documents={getDocumentsForSurgery(
                            patientDocuments,
                            surgery.surgeryId,
                          )}
                          onDocumentClick={handleDocumentClick}
                        />
                      )
                    : undefined
                }
                getRowCanExpand={isSurgeryCategory ? () => true : undefined}
              />
              <Button
                variant="danger"
                disabled={selectedIds.length === 0}
                onClick={() => setIsDeleteModalOpen(true)}
              >
                Διαγραφή επιλεγμένων ({selectedIds.length})
              </Button>
            </>
          )}
        </div>

        {/* Modal ειδοποίησης για έγγραφα χωρίς φυσικό αρχείο */}
        <ErrorSuccessModal
          isOpen={isDocModalOpen}
          type="error" // ή 'warning' ανάλογα με το τι προτιμάς
          title="Προσοχή"
          message={docModalMessage}
          onClose={() => setIsDocModalOpen(false)}
        />
        <GenericPrintModal
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          title={printConfig.title}
          columns={printConfig.columns}
          data={printConfig.data}
        />

        <PatientFormModal
          isOpen={isModalOpen}
          onClose={closeFormModal}
          onSubmit={handleSubmit}
          fields={fields}
          formData={formData}
          setFormData={setFormData}
          isPatientCategory={isPatientCategory}
          editingId={editingId}
          category={category}
          isLoading={current.isLoading}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsDeleteModalOpen(false)}
        />

        {current.modalState && (
          <ErrorSuccessModal
            isOpen={current.modalState.isOpen}
            type={current.modalState.type}
            title={current.modalState.title}
            message={current.modalState.message}
            onClose={current.closeModal}
          />
        )}
      </div>
    </Layout>
  );
};

export default PatientDetails;
